import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Radio, Upload, message, Space, Card, Spin } from 'antd';
import { 
  EditOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  UploadOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import adminRequest from '../../utils/adminRequest';

const { TextArea } = Input;

const EditQuestionModal = ({ 
  visible, 
  onClose, 
  question, 
  onSuccess 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [correctOption, setCorrectOption] = useState(1);
  const [questionImageFile, setQuestionImageFile] = useState(null);
  const [optionImageFiles, setOptionImageFiles] = useState({});

  useEffect(() => {
    if (visible && question) {
      // Initialize form with question data
      form.setFieldsValue({
        question_text: question.question_text || ''
      });

      // Initialize options
      const initialOptions = (question.options || []).map((opt, idx) => ({
        id: opt.id,
        optionText: opt.optionText || '',
        objectKey: opt.objectKey || null,
        imageUrl: opt.imageUrl || null,
        isCorrect: opt.optionValue > 0
      }));
      setOptions(initialOptions);

      // Find correct option index
      const correctIdx = initialOptions.findIndex(opt => opt.isCorrect);
      setCorrectOption(correctIdx >= 0 ? correctIdx + 1 : 1);

      // Reset image files
      setQuestionImageFile(null);
      setOptionImageFiles({});
    }
  }, [visible, question, form]);

  const handleAddOption = () => {
    if (options.length >= 6) {
      message.warning('Maximum 6 options allowed');
      return;
    }
    setOptions([...options, { optionText: '', objectKey: null, imageUrl: null }]);
  };

  const handleRemoveOption = (index) => {
    if (options.length <= 2) {
      message.warning('Minimum 2 options required');
      return;
    }
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
    
    // Adjust correct option if needed
    if (correctOption > newOptions.length) {
      setCorrectOption(newOptions.length);
    } else if (correctOption === index + 1) {
      setCorrectOption(1);
    } else if (correctOption > index + 1) {
      setCorrectOption(correctOption - 1);
    }
  };

  const handleOptionTextChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], optionText: value };
    setOptions(newOptions);
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      
      // Validate options
      if (options.length < 2) {
        message.error('At least 2 options are required');
        return;
      }

      const hasEmptyOption = options.some(opt => !opt.optionText.trim() && !opt.objectKey && !opt.imageUrl);
      if (hasEmptyOption) {
        message.error('All options must have text or an image');
        return;
      }

      // Check for duplicate option texts
      const optionTexts = options.filter(opt => opt.optionText.trim()).map(opt => opt.optionText.trim().toLowerCase());
      const duplicates = optionTexts.filter((text, index) => optionTexts.indexOf(text) !== index);
      if (duplicates.length > 0) {
        message.error('Two or more options have the same answer text. Please make each option unique.');
        return;
      }

      setLoading(true);

      // Prepare the request payload
      const payload = {
        question_id: question.question_id,
        question_text: form.getFieldValue('question_text'),
        correct_opt: correctOption,
        options: options.map(opt => ({
          option_text: opt.optionText,
          option_image_key: opt.objectKey || null
        }))
      };

      // If there's a new question image, we'll need to handle it
      if (questionImageFile) {
        // For now, we'll skip image upload in this version
        // This can be extended to upload images first
        message.info('Image upload during edit will be available in the next version');
      }

      const response = await adminRequest.put(
        '/assessment/editCustomAssessmentQuestion',
        payload
      );

      if (response.data && response.data.message) {
        message.success(response.data.message || 'Question updated successfully');
        onSuccess && onSuccess();
        onClose();
      } else {
        throw new Error('Unexpected response format');
      }

    } catch (error) {
      console.error('Error updating question:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EditOutlined style={{ fontSize: 18, color: '#1890ff' }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>Edit Question</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose} icon={<CloseOutlined />}>
          Cancel
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit} 
          loading={loading}
          icon={<SaveOutlined />}
        >
          Save Changes
        </Button>
      ]}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {/* Question Text */}
          <Form.Item
            name="question_text"
            label="Question Text"
            rules={[{ required: true, message: 'Please enter the question text' }]}
          >
            <TextArea
              rows={3}
              placeholder="Enter the question text..."
              style={{ fontSize: 14 }}
            />
          </Form.Item>

          {/* Question Image Preview */}
          {question?.question_image_url && (
            <Form.Item label="Current Question Image">
              <img 
                src={question.question_image_url} 
                alt="Question" 
                style={{ 
                  maxWidth: 300, 
                  maxHeight: 150, 
                  borderRadius: 8, 
                  border: '1px solid #e8e8e8' 
                }} 
              />
            </Form.Item>
          )}

          {/* Options Section */}
          <Form.Item label="Answer Options">
            <div style={{ 
              border: '1px solid #e8e8e8', 
              borderRadius: 8, 
              padding: 16,
              background: '#fafafa'
            }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {options.map((option, index) => (
                  <Card
                    key={index}
                    size="small"
                    style={{
                      borderColor: correctOption === index + 1 ? '#52c41a' : '#e8e8e8',
                      background: correctOption === index + 1 ? '#f6ffed' : '#fff'
                    }}
                    bodyStyle={{ padding: 12 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {/* Option Letter */}
                      <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: correctOption === index + 1 ? '#52c41a' : '#d9d9d9',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: 12,
                        flexShrink: 0
                      }}>
                        {String.fromCharCode(65 + index)}
                      </div>

                      {/* Option Text Input */}
                      <Input
                        value={option.optionText}
                        onChange={(e) => handleOptionTextChange(index, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + index)}`}
                        style={{ flex: 1 }}
                      />

                      {/* Mark as Correct */}
                      <Radio
                        checked={correctOption === index + 1}
                        onChange={() => setCorrectOption(index + 1)}
                      >
                        Correct
                      </Radio>

                      {/* Remove Option */}
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveOption(index)}
                        disabled={options.length <= 2}
                      />
                    </div>

                    {/* Option Image Preview */}
                    {option.imageUrl && (
                      <div style={{ marginTop: 8, marginLeft: 40 }}>
                        <img 
                          src={option.imageUrl} 
                          alt={`Option ${String.fromCharCode(65 + index)}`}
                          style={{ 
                            maxWidth: 100, 
                            maxHeight: 60, 
                            borderRadius: 4,
                            border: '1px solid #e8e8e8'
                          }} 
                        />
                      </div>
                    )}
                  </Card>
                ))}

                {/* Add Option Button */}
                <Button
                  type="dashed"
                  onClick={handleAddOption}
                  icon={<PlusOutlined />}
                  style={{ width: '100%', marginTop: 8 }}
                  disabled={options.length >= 6}
                >
                  Add Option
                </Button>
              </Space>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditQuestionModal;
